import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatDatepicker } from '@angular/material/datepicker';
import { SelectOption } from '../../../core/models/select-option.model';
import {
  BulkUpdateCaxtonCardsRequest,
  BulkUpdateCaxtonCardsResult,
  CaxtonCardDelivery,
  CaxtonCardOffice
} from '../../../core/models/card-tracker.model';
import { TrialAutocomplete } from '../../../shared/trial-autocomplete/trial-autocomplete.model';
import { LogHelper } from '../../../core/helpers/log.helper';
import { SiteService } from '../../../core/services/site.service';
import { AlertService } from '../../../shared/alert/alert.service';
import { CaxtonCardService } from '../../../core/services/caxton-card.service';
import { DropdownInputComponent } from '../../../shared/dropdown-input/dropdown-input.component';
import { TrialAutocompleteComponent } from '../../../shared/trial-autocomplete/trial-autocomplete.component';
import { ModalV2Component } from '../../../shared/modal-v2/modal-v2.component';
import { fromBeforeToCardNumberValidator } from '../../../core/validators/from-before-to-card-number.validator';
import { maxTotalCardRangeValidator } from '../../../core/validators/max-total-card-range.validator';
import { GlobalConstants, SHORT_DATE_FORMAT } from '../../../core/constants/global-constants';
import moment, { Moment } from 'moment';

@Component({
  selector: 'app-caxton-card-bulk-update-modal',
  templateUrl: './caxton-card-bulk-update-modal.component.html',
  styleUrl: './caxton-card-bulk-update-modal.component.scss',
  providers: [provideMomentDateAdapter(SHORT_DATE_FORMAT)]
})
export class CaxtonCardBulkUpdateModalComponent {
  @ViewChild('bulkUpdateModal') bulkUpdateModal: ModalV2Component;
  @ViewChild('trialAutocomplete') trialAutocomplete: TrialAutocompleteComponent;
  @ViewChild('siteAutocomplete') siteAutocomplete: DropdownInputComponent;
  @ViewChild('officeDropdown') officeDropdown: DropdownInputComponent;

  @Output() changed = new EventEmitter<void>();

  constructor(
    private _siteService: SiteService,
    private _alertService: AlertService,
    private _caxtonCardService: CaxtonCardService
  ) {}

  protected officeOptions: SelectOption[] = [
    new SelectOption(CaxtonCardOffice.UK.toString(), 'UK'),
    new SelectOption(CaxtonCardOffice.US.toString(), 'US')
  ];

  protected assignCardsSiteOptions: SelectOption[] = [];
  protected result: { updatedCount: number; missingNumbers: number[]; rangesLabel: string } | null = null;
  protected readonly maxRangeSize = GlobalConstants.maxCaxtonCardBulkUpdateRangeSize;

  private readonly updateFormValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    const anyEnabled =
      ['applyExpiry', 'applyOffice', 'applyAssign'].some(k => group.get(k)?.value) ||
      !!group.get('deliveryAction')?.value;

    if (!anyEnabled)
    {
      return { noSectionEnabled: true };
    }

    if (group.get('applyExpiry')?.value && !group.get('expiryDate')?.value)
    {
      return { expiryDateRequired: true };
    }

    if (group.get('applyAssign')?.value && !group.get('trialId')?.value)
    {
      return { trialRequired: true };
    }

    return null;
  };

  protected rangesForm = new FormGroup({
    ranges: new FormArray<FormGroup>([this.createRangeRow()])
  }, { validators: maxTotalCardRangeValidator(GlobalConstants.maxCaxtonCardBulkUpdateRangeSize) });

  protected updateForm = new FormGroup({
    isBusy: new FormControl<boolean>(false),

    applyExpiry: new FormControl<boolean>(false),
    expiryDate: new FormControl(null),

    applyOffice: new FormControl<boolean>(false),
    office: new FormControl<string>(null),

    // 'received' | 'sent' | 'missing' | null — null means no delivery update
    deliveryAction: new FormControl<string>(null),
    courierCost: new FormControl<string>(null),
    trackingNumber: new FormControl<string>(null),

    applyAssign: new FormControl<boolean>(false),
    trialId: new FormControl<string>(null),
    siteId: new FormControl<string>(null),
    siteContact: new FormControl<string>(null),
    patientDetails: new FormControl<string>(null),
    sendToPatient: new FormControl<boolean>(false)
  }, { validators: this.updateFormValidator });

  get ranges(): FormArray<FormGroup> {
    return this.rangesForm.get('ranges') as FormArray<FormGroup>;
  }

  get isSubmitDisabled(): boolean {
    return this.updateForm.get('isBusy').value || this.rangesForm.invalid || this.updateForm.invalid;
  }

  show(): void {
    this.result = null;

    while (this.ranges.length > 1)
    {
      this.ranges.removeAt(1);
    }
    this.ranges.at(0).reset();

    this.updateForm.reset({
      isBusy: false,
      applyExpiry: false,
      expiryDate: null,
      applyOffice: false,
      office: null,
      deliveryAction: null,
      courierCost: null,
      trackingNumber: null,
      applyAssign: false,
      trialId: null,
      siteId: null,
      siteContact: null,
      patientDetails: null,
      sendToPatient: false
    });

    this.officeDropdown?.reset();
    this.trialAutocomplete?.reset();
    this.siteAutocomplete?.reset();
    this.assignCardsSiteOptions = [];

    this.bulkUpdateModal.show();
  }

  addRange(): void {
    this.ranges.push(this.createRangeRow());
  }

  removeRange(index: number): void {
    if (this.ranges.length > 1)
    {
      this.ranges.removeAt(index);
    }
  }

  setDeliveryAction(value: string): void {
    const current = this.updateForm.get('deliveryAction').value;
    this.updateForm.get('deliveryAction').setValue(current === value ? null : value);
  }

  clearDeliveryAction(): void {
    this.updateForm.get('deliveryAction').setValue(null);
  }

  onAssignCardTrialFilterChanged(selection: TrialAutocomplete): void {
    this.updateForm.get('siteId').setValue(null);
    this.assignCardsSiteOptions = [];
    this.siteAutocomplete?.reset();

    if (!selection)
    {
      this.updateForm.get('trialId').setValue(null);
      return;
    }

    this.updateForm.get('trialId').setValue(selection.id);

    this._siteService.retrieveSites(1, selection.id, null, null, false, null, 9999).subscribe({
      next: response => {
        this.assignCardsSiteOptions = response.results.map(r => new SelectOption(r.id, `${r.name}-${r.address}`));
      },
      error: error => {
        this._alertService.showWarningAlert('Failed to get sites. Please try again or contact support');
        LogHelper.log(error);
      }
    });
  }

  setExpiryMonthAndYear(normalizedMonthAndYear: Moment, datepicker: MatDatepicker<Moment>): void {
    const ctrlValue = moment();
    ctrlValue.month(normalizedMonthAndYear.month());
    ctrlValue.year(normalizedMonthAndYear.year());
    this.updateForm.get('expiryDate').setValue(ctrlValue);
    datepicker.close();
  }

  clearOffice(): void {
    this.updateForm.get('office').setValue(null);
    this.officeDropdown?.reset();
  }

  onSubmit(): void {
    if (this.isSubmitDisabled)
    {
      return;
    }

    const f = this.updateForm;
    const ranges = this.ranges.controls.map(row => ({
      fromCardNumber: row.get('fromCardNumber').value as number,
      toCardNumber: row.get('toCardNumber').value as number
    }));

    const request: BulkUpdateCaxtonCardsRequest = { ranges };

    if (f.get('applyExpiry').value)
    {
      const d = f.get('expiryDate').value;
      request.expiryDate = new Date(d.year(), d.month(), 15);
    }

    if (f.get('applyOffice').value)
    {
      request.office = this.toOffice(f.get('office').value);
    }

    const deliveryAction = f.get('deliveryAction').value;
    if (deliveryAction === 'received')
    {
      request.delivery = CaxtonCardDelivery.Received;
    }

    if (deliveryAction === 'sent')
    {
      request.delivery = CaxtonCardDelivery.Sent;
      request.courierCost = f.get('courierCost').value;
      request.trackingNumber = f.get('trackingNumber').value;
    }

    if (deliveryAction === 'missing')
    {
      request.delivery = CaxtonCardDelivery.Missing;
    }

    if (f.get('applyAssign').value)
    {
      request.trialId = f.get('trialId').value;
      request.siteId = f.get('siteId').value;
      request.siteContact = f.get('siteContact').value;
      request.patientDetails = f.get('patientDetails').value;
      request.sendToPatient = f.get('sendToPatient').value;
    }

    f.patchValue({ isBusy: true });

    this._caxtonCardService.bulkUpdateCards(request).subscribe({
      next: (response: BulkUpdateCaxtonCardsResult) => {
        f.patchValue({ isBusy: false });
        this.result = {
          updatedCount: response.updatedCount,
          missingNumbers: response.missingCardNumbers,
          rangesLabel: ranges.map(r => `${r.fromCardNumber}–${r.toCardNumber}`).join(', ')
        };
        this.changed.emit();
      },
      error: error => {
        f.patchValue({ isBusy: false });
        this._alertService.showErrorResponse(error.error);
      }
    });
  }

  closeResult(): void {
    this.bulkUpdateModal.hide();
    this.result = null;
  }

  private createRangeRow(): FormGroup {
    return new FormGroup({
      fromCardNumber: new FormControl<number>(null, [Validators.required, Validators.min(1)]),
      toCardNumber: new FormControl<number>(null, [Validators.required, Validators.min(1)])
    }, { validators: fromBeforeToCardNumberValidator });
  }

  private toOffice(value: string | null): CaxtonCardOffice | null {
    return value != null && value !== '' ? Number(value) as CaxtonCardOffice : null;
  }
}
